import React, { useState, useEffect, useCallback } from 'react';
import {
    Users, Loader2, Search, Plus, Edit, Trash2, Save, X, ToggleLeft, ToggleRight,
    Upload, MessageCircle, ArrowUp, ArrowDown, Eye, EyeOff, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { API, API_BASE } from '../../config/apiBase';

const CATEGORIES = ["Designer", "Marketing", "Social Media", "Gestor de Tráfego", "Editor", "Copywriter", "Automação", "Suporte técnico"];
const PLANS = ["essencial", "gestão", "pro", "enterprise"];

const EMPTY_FORM = { name: '', specialty: '', description: '', whatsapp: '', category: 'Designer', order: 0, active: true, visible_plans: [...PLANS] };

export default function AdminIndicaçõesPage() {
    const [pros, setPros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [saving, setSaving] = useState(false);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const fetchPros = useCallback(async () => {
        try {
            const res = await fetch(`${API}/admin/profissionais`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) setPros(await res.json());
        } catch {} finally { setLoading(false); }
    }, [token]);

    useEffect(() => { fetchPros(); }, [fetchPros]);

    const openNew = () => { setEditingId(null); setForm({ ...EMPTY_FORM }); setShowModal(true); };
    const openEdit = (p) => {
        setEditingId(p.profissional_id);
        setForm({ name: p.name, specialty: p.specialty || '', description: p.description || '', whatsapp: p.whatsapp || '', category: p.category || 'Designer', order: p.order || 0, active: p.active !== false, visible_plans: p.visible_plans || [...PLANS] });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error('Nome obrigatorio'); return; }
        setSaving(true);
        try {
            const url = editingId ? `${API}/admin/profissionais/${editingId}` : `${API}/admin/profissionais`;
            const method = editingId ? 'PUT' : 'POST';
            const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
            if (res.ok) { toast.success(editingId ? 'Atualizado' : 'Criado'); setShowModal(false); fetchPros(); }
            else { const e = await res.json(); toast.error(e.detail || 'Erro'); }
        } catch { toast.error('Erro de conexão'); } finally { setSaving(false); }
    };

    const deletePro = async (id) => {
        if (!window.confirm('Excluir profissional?')) return;
        const res = await fetch(`${API}/admin/profissionais/${id}`, { method: 'DELETE', headers });
        if (res.ok) { toast.success('Excluido'); fetchPros(); }
    };

    const toggleActive = async (p) => {
        const res = await fetch(`${API}/admin/profissionais/${p.profissional_id}`, { method: 'PUT', headers, body: JSON.stringify({ active: !p.active }) });
        if (res.ok) { fetchPros(); }
    };

    const uploadPhoto = async (profId) => {
        const input = document.createElement('input');
        input.type = 'file'; input.accept = 'image/png,image/jpeg,image/webp';
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const fd = new FormData(); fd.append('file', file);
            const res = await fetch(`${API}/admin/profissionais/${profId}/photo`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
            if (res.ok) { toast.success('Foto enviada'); fetchPros(); } else toast.error('Erro ao enviar foto');
        };
        input.click();
    };

    const togglePlan = (plan) => {
        setForm(prev => ({
            ...prev,
            visible_plans: prev.visible_plans.includes(plan) ? prev.visible_plans.filter(p => p !== plan) : [...prev.visible_plans, plan],
        }));
    };

    const filtered = pros.filter(p => !searchText || p.name?.toLowerCase().includes(searchText.toLowerCase()) || p.category?.toLowerCase().includes(searchText.toLowerCase()));

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div className="space-y-4" data-testid="admin-indicações-page">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs" style={{ color: 'var(--muted)' }}>{pros.length} profissional(is) cadastrado(s)</p>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} onClick={openNew} data-testid="new-prof-btn">
                    <Plus size={13} /> Novo Profissional
                </button>
            </div>

            {/* Search */}
            <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted)' }} />
                <input type="text" placeholder="Buscar nome ou categoria..." value={searchText} onChange={e => setSearchText(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="prof-search" />
            </div>

            {/* Stats */}
            <div className="flex gap-2 flex-wrap">
                {CATEGORIES.map(c => {
                    const count = pros.filter(p => p.category === c).length;
                    if (count === 0) return null;
                    return (
                        <span key={c} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)', color: 'var(--sv-gold-primary)', border: '1px solid rgba(var(--sv-accent-rgb), 0.15)' }}>
                            {c} ({count})
                        </span>
                    );
                })}
            </div>

            {/* List */}
            {filtered.length === 0 ? (
                <div className="text-center py-14 rounded-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <Users size={32} className="mx-auto mb-2" style={{ color: 'var(--muted)', opacity: 0.4 }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Nenhum profissional</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Cadastre profissionais para exibir na aba Indicações</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map(p => (
                        <div key={p.profissional_id} className="rounded-xl p-3.5 transition-all" style={{ background: 'var(--surface)', border: '1px solid var(--border)', opacity: p.active !== false ? 1 : 0.5 }} data-testid={`prof-${p.profissional_id}`}>
                            <div className="flex items-center gap-3">
                                {/* Photo */}
                                <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 cursor-pointer" onClick={() => uploadPhoto(p.profissional_id)} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                                    {p.photo_url ? (
                                        <img src={`${API_BASE}${p.photo_url}`} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center"><Upload size={14} style={{ color: 'var(--muted)' }} /></div>
                                    )}
                                </div>
                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{p.name}</span>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)', color: 'var(--sv-gold-primary)' }}>{p.category}</span>
                                        {(p.avg_rating || 0) > 0 && (
                                            <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.06)', color: 'var(--sv-gold-primary)' }}>
                                                <Star size={9} fill="#F5B544" style={{ color: '#F5B544' }} /> {p.avg_rating?.toFixed(1)} ({p.total_reviews})
                                            </span>
                                        )}
                                        {!p.active && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>Inativo</span>}
                                    </div>
                                    <p className="text-xs" style={{ color: 'var(--muted)' }}>{p.specialty}</p>
                                    {p.description && <p className="text-[11px] mt-0.5 truncate" style={{ color: 'var(--muted)', opacity: 0.7 }}>{p.description}</p>}
                                    <div className="flex gap-1 mt-1 flex-wrap">
                                        {(p.visible_plans || []).map(pl => (
                                            <span key={pl} className="text-[9px] px-1 py-0.5 rounded capitalize" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }}>{pl}</span>
                                        ))}
                                    </div>
                                </div>
                                {/* Actions */}
                                <div className="flex items-center gap-1 shrink-0">
                                    <span className="text-[10px] mr-1" style={{ color: 'var(--muted)' }}>#{p.order}</span>
                                    <button className="p-1.5 rounded-lg" onClick={() => toggleActive(p)} title={p.active ? 'Desativar' : 'Ativar'}>
                                        {p.active !== false ? <ToggleRight size={16} style={{ color: '#22c55e' }} /> : <ToggleLeft size={16} style={{ color: '#ef4444' }} />}
                                    </button>
                                    <button className="p-1.5 rounded-lg" onClick={() => openEdit(p)} data-testid={`edit-prof-${p.profissional_id}`}><Edit size={13} style={{ color: 'var(--muted)' }} /></button>
                                    <button className="p-1.5 rounded-lg" onClick={() => deletePro(p.profissional_id)}><Trash2 size={13} style={{ color: '#ef4444' }} /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
                    <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl p-5" style={{ background: 'var(--surface)', border: '1px solid rgba(var(--sv-accent-rgb), 0.25)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }} data-testid="prof-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>{editingId ? 'Editar Profissional' : 'Novo Profissional'}</h3>
                            <button onClick={() => setShowModal(false)} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }}><X size={14} style={{ color: 'var(--muted)' }} /></button>
                        </div>
                        <div className="flex flex-col gap-3">
                            <div>
                                <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Nome</label>
                                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nome completo"
                                    className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="prof-name" />
                            </div>
                            <div>
                                <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Especialidade</label>
                                <input type="text" value={form.specialty} onChange={e => setForm({ ...form, specialty: e.target.value })} placeholder="Ex: Identidade Visual, Branding"
                                    className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="prof-specialty" />
                            </div>
                            <div>
                                <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Descrição curta</label>
                                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Breve descrição do profissional..."
                                    className="w-full px-3 py-2.5 rounded-lg text-sm resize-none" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="prof-desc" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--muted)' }}>WhatsApp</label>
                                    <input type="text" value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} placeholder="5511999999999"
                                        className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="prof-whatsapp" />
                                </div>
                                <div>
                                    <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Ordem</label>
                                    <input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                                        className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="prof-order" />
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] font-medium mb-1.5 block" style={{ color: 'var(--muted)' }}>Categoria</label>
                                <div className="flex gap-1.5 flex-wrap">
                                    {CATEGORIES.map(c => (
                                        <button key={c} className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all"
                                            style={{ background: form.category === c ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface-2)', border: `1px solid ${form.category === c ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}`, color: form.category === c ? 'var(--sv-accent)' : 'var(--muted)' }}
                                            onClick={() => setForm({ ...form, category: c })}>{c}</button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] font-medium mb-1.5 block" style={{ color: 'var(--muted)' }}>Visivel nos planos</label>
                                <div className="flex gap-1.5 flex-wrap">
                                    {PLANS.map(pl => {
                                        const active = form.visible_plans.includes(pl);
                                        return (
                                            <button key={pl} className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium capitalize transition-all"
                                                style={{ background: active ? 'rgba(34,197,94,0.12)' : 'var(--surface-2)', border: `1px solid ${active ? 'rgba(34,197,94,0.3)' : 'var(--border)'}`, color: active ? '#22c55e' : 'var(--muted)' }}
                                                onClick={() => togglePlan(pl)}>{pl}</button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button className="px-4 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }} onClick={() => setShowModal(false)}>Cancelar</button>
                            <button className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} onClick={handleSave} disabled={saving} data-testid="prof-save-btn">
                                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} {editingId ? 'Salvar' : 'Criar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
